/**
 * Dorpi Map Selector
 * Handles Leaflet map, location search, bounding box selection, and STL generation
 */

/**
 * Loading stages, matched to the server's SSE progress events.
 *
 * `target` is the bar width (%) the stage fills to; `duration` (seconds) is the
 * stage's average real-world length, over which the bar eases toward `target`.
 *
 * Only the final `save` stage is driven by the real server progress event. The
 * pre-save stages (download → mesh) are faked on a timer: the bar climbs to
 * mesh's target over their combined average durations, then holds there until the
 * real `save` event arrives and drives the bar the rest of the way to 100%.
 */
// Targets are front-loaded: early stages claim a big share of the bar so it
// sprints up front (feels fast), then decelerates through mesh and save. The
// per-second fill rate decreases monotonically down this list.
const DORPI_LOADING_STAGES = {
  download:     { target: 25,  duration: 4,  label: "Hoogtekaart downloaden" },
  load_terrain: { target: 40,  duration: 4,  label: "Terreindata inladen" },
  buildings:    { target: 50,  duration: 4,  label: "Gebouwen verwerken" },
  process:      { target: 60,  duration: 6,  label: "Data opschonen" },
  mesh:         { target: 78,  duration: 18, label: "3D-model genereren" },
  save:         { target: 100, duration: 30, label: "Model opslaan" },
};

class DorpiMapSelector {
  constructor(wrapper) {
    this.wrapper = wrapper;
    this.config = this.parseConfig();
    this.state = {
      map: null,
      currentBbox: null,
      center: null,
      aspectRatio: "portrait", // 'portrait' or 'landscape'
      frame: "zwart",
      size: this.config.defaultSize,
      isGenerating: false,
      isLocating: false,
      isFullscreen: false,
      searchTimeout: null,
    };

    this.elements = this.cacheElements();
    this.init();
  }

  parseConfig() {
    return {
      apiUrl: this.wrapper.dataset.apiUrl || "http://api.dorpi.nl",
      defaultLat: parseFloat(this.wrapper.dataset.defaultLat) || 52.3676,
      defaultLng: parseFloat(this.wrapper.dataset.defaultLng) || 4.9041,
      defaultZoom: parseInt(this.wrapper.dataset.defaultZoom, 10) || 15,
      minSize: parseInt(this.wrapper.dataset.minSize, 10) || 300,
      maxSize: parseInt(this.wrapper.dataset.maxSize, 10) || 1800,
      defaultSize: parseInt(this.wrapper.dataset.defaultSize, 10) || 500,
      viewerUrl: this.wrapper.dataset.viewerUrl || "/pages/model-viewer",
    };
  }

  cacheElements() {
    return {
      mapContainer: this.wrapper.querySelector("[data-map-container]"),
      searchInput: this.wrapper.querySelector("[data-search-input]"),
      searchResults: this.wrapper.querySelector("[data-search-results]"),
      geoBtn: this.wrapper.querySelector("[data-geo-btn]"),
      geoBtnLabel: this.wrapper.querySelector("[data-geo-btn-label]"),
      geoMsg: this.wrapper.querySelector("[data-geo-msg]"),
      ratioButtons: this.wrapper.querySelectorAll("[data-ratio]"),
      frameButtons: this.wrapper.querySelectorAll("[data-frame]"),
      sizeSlider: this.wrapper.querySelector("[data-size-slider]"),
      sizeDisplay: this.wrapper.querySelector("[data-size-display]"),
      generateBtn: this.wrapper.querySelector("[data-generate-btn]"),
      previewImg: this.wrapper.querySelector("[data-preview-img]"),
      error: this.wrapper.querySelector("[data-error]"),
      loading: this.wrapper.querySelector("[data-loading]"),
      loadingBar: this.wrapper.querySelector("[data-loading-bar]"),
      loadingMsg: this.wrapper.querySelector("[data-loading-msg]"),
      loadingMessagesTpl: this.wrapper.querySelector(
        "template[data-loading-messages]"
      ),
      bboxRect: null, // set in createBboxOverlay
    };
  }

  init() {
    if (typeof L === "undefined") {
      console.error("Leaflet is not loaded");
      return;
    }

    this.pageEntryTime = performance.now();
    this.trackEvent("dorpi_map_selector_view");

    this.initMap();
    this.bindEvents();
    this.updateSliderBackground();
  }

  initMap() {
    const { defaultLat, defaultLng, defaultZoom } = this.config;

    this.state.map = L.map(this.elements.mapContainer, {
      zoomSnap: 0,
      minZoom: 1, // overwritten by computeMinZoom() once the map is ready
    }).setView([defaultLat, defaultLng], defaultZoom);

    L.tileLayer(
      "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=cb1_2atf_1_81e9ccedf7913bc94397e9ce",
      {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
        subdomains: "abcd",
        maxZoom: 19,
        keepBuffer: 6,
        updateWhenZooming: false,
      },
    ).addTo(this.state.map);

    // Recalculate container dimensions after the browser has fully laid out
    // the page (catches cases where min-height or flex sizing resolves late).
    setTimeout(() => this.state.map.invalidateSize(), 0);

    // moveend covers both pan and zoom — no need for a separate zoomend handler
    this.state.map.on("moveend", () => this.updateBboxFromCenter());

    this.createBboxOverlay();
    this.createFullscreenControl();
    this.createMinZoomTooltip();
    this.enableTwoFingerPanOnly();
    this.updateRectPixelSize();

    // Now that the overlay exists, compute the real limits and initial zoom
    this.state.map.options.minZoom = this.computeMinZoom();
    const initialZoom = this.computeOptimalZoom();
    this.state.map.setView([defaultLat, defaultLng], initialZoom, { animate: false });
  }

  createBboxOverlay() {
    const overlay = document.createElement("div");
    overlay.className = "dorpi-bbox-overlay";

    const rect = document.createElement("div");
    rect.className = "dorpi-bbox-rect";
    overlay.appendChild(rect);

    this.elements.mapContainer.appendChild(overlay);
    this.elements.bboxRect = rect;
  }

  /**
   * Small toast warning shown when the visitor tries to zoom out past the
   * limit (maxSize meters). Bottom-center of the map so it never competes
   * with the zoom control (top-left) or the fullscreen button (top-right).
   */
  createMinZoomTooltip() {
    const msg = document.createElement("p");
    msg.className = "dorpi-map-selector__min-zoom-msg";
    msg.setAttribute("role", "status");
    msg.setAttribute("aria-live", "polite");

    const title = document.createElement("strong");
    title.className = "dorpi-map-selector__min-zoom-title";
    title.textContent = "Kan niet verder uitzoomen";

    const subtitle = document.createElement("span");
    subtitle.className = "dorpi-map-selector__min-zoom-subtitle";
    subtitle.textContent = "gebouwen zouden te klein worden";

    msg.appendChild(title);
    msg.appendChild(subtitle);
    this.elements.mapContainer.appendChild(msg);
    this.elements.minZoomMsg = msg;

    this.bindMinZoomDetection();
  }

  /**
   * Leaflet silently clamps zoom-out attempts past `minZoom` — it doesn't
   * fire an event for a blocked attempt, only for an actual zoom change. So
   * the attempt itself is detected from the raw input instead: a mouse
   * wheel/trackpad scroll with deltaY > 0 (zoom out), or two touches moving
   * closer together (pinch in), while the map is already at (or essentially
   * at) its minimum zoom.
   */
  bindMinZoomDetection() {
    const container = this.elements.mapContainer;

    const atMinZoom = () =>
      this.state.map.getZoom() <= this.state.map.getMinZoom() + 0.01;

    container.addEventListener(
      "wheel",
      (e) => {
        if (e.deltaY > 0 && atMinZoom()) this.showMinZoomTooltip();
      },
      { passive: true },
    );

    let pinchDist = null;
    container.addEventListener(
      "touchmove",
      (e) => {
        if (e.touches.length !== 2) {
          pinchDist = null;
          return;
        }
        const [a, b] = e.touches;
        const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
        if (pinchDist != null && dist < pinchDist - 2 && atMinZoom()) {
          this.showMinZoomTooltip();
        }
        pinchDist = dist;
      },
      { passive: true },
    );
    container.addEventListener("touchend", () => {
      pinchDist = null;
    });
  }

  /**
   * Show the min-zoom warning for 3 seconds, then enforce a 10-second
   * cooldown (counted from this trigger) during which further zoom-out
   * attempts are ignored — it won't show again until the cooldown lapses,
   * even while the visitor keeps scrolling/pinching past the limit.
   */
  showMinZoomTooltip() {
    const el = this.elements.minZoomMsg;
    if (!el) return;

    const now = Date.now();
    if (this._minZoomCooldownUntil && now < this._minZoomCooldownUntil) return;
    this._minZoomCooldownUntil = now + 10000;

    el.classList.add("is-visible");
    setTimeout(() => el.classList.remove("is-visible"), 3000);
  }

  /**
   * On touch devices, a single finger inside the map should scroll the page
   * normally — only a two-finger touch should pan/zoom the map. Leaflet has
   * no such option built in, so a capture-phase listener on an ancestor of
   * the map intercepts every single-finger touchstart before it ever
   * reaches Leaflet's own listeners (attached on the map container itself),
   * via stopPropagation — never preventDefault, so the browser's native
   * scroll proceeds untouched. A two-finger touchstart is let through
   * as-is, so Leaflet's normal drag + pinch-zoom handling takes over
   * exactly as before. The matching CSS `touch-action` override lives next
   * to the Leaflet overrides in the stylesheet.
   */
  enableTwoFingerPanOnly() {
    this.wrapper.addEventListener(
      "touchstart",
      (e) => {
        if (e.touches.length < 2 && this.elements.mapContainer.contains(e.target)) {
          e.stopPropagation();
        }
      },
      { capture: true },
    );
  }

  /**
   * Windowed fullscreen toggle for the map — a plain CSS expand to cover the
   * viewport (not the browser Fullscreen API), so it works the same on
   * mobile and desktop with no permission prompt. Two buttons share the same
   * corner: the expand button shown normally, and a back button shown only
   * while fullscreen, which returns to the original view.
   */
  createFullscreenControl() {
    const expandBtn = document.createElement("button");
    expandBtn.type = "button";
    expandBtn.className = "dorpi-map-selector__fullscreen-btn";
    expandBtn.setAttribute("aria-label", "Volledig scherm");
    expandBtn.title = "Volledig scherm";
    expandBtn.innerHTML =
      '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M8 3H5a2 2 0 0 0-2 2v3"/><path d="M21 8V5a2 2 0 0 0-2-2h-3"/><path d="M3 16v3a2 2 0 0 0 2 2h3"/><path d="M16 21h3a2 2 0 0 0 2-2v-3"/></svg>';

    const backBtn = document.createElement("button");
    backBtn.type = "button";
    backBtn.className = "dorpi-map-selector__fullscreen-back-btn";
    backBtn.setAttribute("aria-label", "Terug naar het overzicht");
    backBtn.title = "Terug naar het overzicht";
    backBtn.innerHTML =
      '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>';

    expandBtn.addEventListener("click", () => this.setFullscreen(true));
    backBtn.addEventListener("click", () => this.setFullscreen(false));

    this.elements.mapContainer.appendChild(expandBtn);
    this.elements.mapContainer.appendChild(backBtn);
    this.elements.fullscreenBtn = expandBtn;
    this.elements.fullscreenBackBtn = backBtn;

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.state.isFullscreen) this.setFullscreen(false);
    });
  }

  /**
   * Enter/exit the windowed fullscreen map. Re-syncs the overlay rectangle
   * and zoom after the container's pixel size changes, exactly like the
   * window resize handler — the physical selection size (meters) is kept,
   * only the on-screen representation adjusts.
   */
  setFullscreen(isFullscreen) {
    if (this.state.isFullscreen === isFullscreen) return;
    this.state.isFullscreen = isFullscreen;
    this.wrapper.classList.toggle("is-fullscreen", isFullscreen);
    document.body.style.overflow = isFullscreen ? "hidden" : "";

    requestAnimationFrame(() => {
      this.state.map.invalidateSize();
      this.updateRectPixelSize();
      const center = this.state.map.getCenter();
      this.state.map.setView(center, this.computeOptimalZoom(), { animate: false });
      // moveend fires synchronously after setView → updateBboxFromCenter
    });

    this.trackEvent(
      isFullscreen ? "dorpi_map_fullscreen_open" : "dorpi_map_fullscreen_close",
    );
  }

  /**
   * Called on map moveend and after size/ratio/zoom changes.
   * Recalculates bbox coordinates from the current map center and physical size.
   * Also keeps minZoom current (it varies with latitude via the cos factor).
   * Does NOT change the visual rectangle size — that is fixed by updateRectPixelSize().
   */
  updateBboxFromCenter() {
    const center = this.state.map.getCenter();
    const { lat, lng } = center;
    const dims = this.calculateBboxDimensions();

    const { latOffset } = this.metersToLatLng(dims.height / 2, lat);
    const { lngOffset } = this.metersToLatLng(dims.width / 2, lat);

    this.state.currentBbox = {
      min_x: lng - lngOffset, // west longitude
      min_y: lat - latOffset, // south latitude
      max_x: lng + lngOffset, // east longitude
      max_y: lat + latOffset, // north latitude
    };

    // Keep Leaflet's scroll-zoom hard limit in sync with maxSize and current latitude
    this.state.map.options.minZoom = this.computeMinZoom();

    // Sync slider to match the physical size the rectangle now represents.
    // This fires on user scroll/pinch zoom as well as programmatic setView calls.
    // The round-trip _zoomForSize(_sizeForZoom(z)) ≈ z, so programmatic calls
    // produce no visible change (newSize === this.state.size after rounding).
    const computedSize = this._sizeForZoom(this.state.map.getZoom());
    const newSize = Math.max(
      this.config.minSize,
      Math.min(this.config.maxSize, Math.round(computedSize)),
    );
    if (newSize !== this.state.size) {
      this.state.size = newSize;
      this.elements.sizeSlider.value = newSize;
      this.elements.sizeDisplay.textContent = newSize;
      this.updateSliderBackground();
    }

    this.updateGenerateButton();
  }

  /**
   * Set the overlay rectangle's pixel dimensions based on aspect ratio and map size.
   * Only needs to be called on init, aspect ratio change, and window resize —
   * not when size or zoom changes.
   */
  updateRectPixelSize() {
    if (!this.elements.bboxRect) return;
    const mapEl = this.elements.mapContainer;
    const mapH = mapEl.clientHeight || 400;
    const mapW = mapEl.clientWidth || 600;

    const SQRT2 = Math.SQRT2;
    const ratio = this.state.aspectRatio === "portrait" ? 1 / SQRT2 : SQRT2;

    const targetFraction = 0.65;
    let pixelH = mapH * targetFraction;
    let pixelW = pixelH * ratio;

    if (pixelW > mapW * targetFraction) {
      pixelW = mapW * targetFraction;
      pixelH = pixelW / ratio;
    }

    this.elements.bboxRect.style.width = `${Math.round(pixelW)}px`;
    this.elements.bboxRect.style.height = `${Math.round(pixelH)}px`;
  }

  /**
   * Shared zoom formula: returns the fractional zoom where the rectangle
   * (at its fixed pixel size) represents exactly `size` meters.
   */
  _zoomForSize(size) {
    const mapEl = this.elements.mapContainer;
    const mapH = mapEl.clientHeight || 400;
    const mapW = mapEl.clientWidth || 600;

    const SQRT2 = Math.SQRT2;
    const ratios = {
      portrait: { width: 1, height: SQRT2 },
      landscape: { width: SQRT2, height: 1 },
    };
    const r = ratios[this.state.aspectRatio];
    const scale = size / Math.max(r.width, r.height);
    const dims = { width: r.width * scale, height: r.height * scale };

    const lat = this.state.map
      ? this.state.map.getCenter().lat
      : this.config.defaultLat;
    const f = 0.65;
    const ppm = Math.min((mapH * f) / dims.height, (mapW * f) / dims.width);

    return Math.log2(
      (40075016.686 * Math.cos((lat * Math.PI) / 180) * ppm) / 256,
    );
  }

  /**
   * Inverse of _zoomForSize: given a zoom level, what physical size (meters)
   * does the fixed-pixel rectangle currently represent?
   */
  _sizeForZoom(zoom) {
    const mapEl = this.elements.mapContainer;
    const mapH = mapEl.clientHeight || 400;
    const mapW = mapEl.clientWidth || 600;
    const lat = this.state.map.getCenter().lat;
    const f = 0.65;
    const SQRT2 = Math.SQRT2;

    // pixelsPerMeter at this zoom and latitude
    const ppm =
      (256 * Math.pow(2, zoom)) /
      (40075016.686 * Math.cos((lat * Math.PI) / 180));

    // Constraining pixel dimension (matches the min() in _zoomForSize)
    const constraining =
      this.state.aspectRatio === "portrait"
        ? Math.min(mapH * f, mapW * f * SQRT2)
        : Math.min(mapH * f * SQRT2, mapW * f);

    return constraining / ppm;
  }

  /** Zoom level that makes the rectangle represent the currently selected size. */
  computeOptimalZoom() {
    return Math.max(10, Math.min(19, this._zoomForSize(this.state.size)));
  }

  /**
   * Minimum zoom the user is allowed to reach via scroll — corresponds to
   * the rectangle representing exactly maxSize meters.
   * Leaflet reads map.options.minZoom in _limitZoom, so updating it here
   * clamps scroll zoom in real time without any snap-back hack.
   */
  computeMinZoom() {
    return this._zoomForSize(this.config.maxSize);
  }

  bindEvents() {
    // Search input with debounce
    this.elements.searchInput.addEventListener("input", (e) => {
      clearTimeout(this.state.searchTimeout);
      this.state.searchTimeout = setTimeout(() => {
        this.searchLocation(e.target.value);
      }, 300);
    });

    this.elements.searchInput.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        this.hideSearchResults();
        this.elements.searchInput.blur();
      }
    });

    this.elements.searchInput.addEventListener("focus", () => {
      if (this.elements.searchResults.children.length > 0) {
        this.elements.searchResults.hidden = false;
      }
    });

    // Current location button — only requests geolocation on explicit click
    if (this.elements.geoBtn) {
      if (navigator.geolocation) {
        this.elements.geoBtn.addEventListener("click", () =>
          this.requestGeolocation(),
        );
      } else {
        // Browser can't provide location — remove the button entirely.
        this.elements.geoBtn.remove();
      }
    }

    // Aspect ratio buttons
    this.elements.ratioButtons.forEach((btn) => {
      btn.addEventListener("click", () =>
        this.setAspectRatio(btn.dataset.ratio),
      );
    });

    // Frame buttons
    this.elements.frameButtons.forEach((btn) => {
      btn.addEventListener("click", () => this.setFrame(btn.dataset.frame));
    });

    // Size slider
    this.elements.sizeSlider.addEventListener("input", (e) => {
      this.setSize(parseInt(e.target.value, 10));
    });

    // Generate button
    this.elements.generateBtn.addEventListener("click", () =>
      this.generateSTL(),
    );

    // Keep the overlay rectangle and slider fill correct when the viewport
    // changes (the track width — and thus the fill offset — depends on it).
    window.addEventListener("resize", () => {
      clearTimeout(this._resizeTimeout);
      this._resizeTimeout = setTimeout(() => {
        this.updateRectPixelSize();
        const center = this.state.map.getCenter();
        this.state.map.setView(center, this.computeOptimalZoom(), { animate: false });
        this.updateSliderBackground();
      }, 150);
    });

    // Time-on-page tracking
    window.addEventListener("beforeunload", () => this.trackTimeOnPage());

    // Click outside to close search results
    document.addEventListener("click", (e) => {
      if (!e.target.closest(".dorpi-map-selector__search-wrapper")) {
        this.hideSearchResults();
      }
    });
  }

  /**
   * Request the visitor's location. Triggered only by an explicit click on the
   * "Huidige locatie" button, so the browser permission prompt appears in context
   * — never on page load. Handles the denied case with a clear message and keeps
   * manual search as the fallback.
   */
  requestGeolocation() {
    if (!navigator.geolocation || this.state.isLocating) return;

    this.state.isLocating = true;
    this.hideGeoMsg();
    this.setGeoBtnLoading(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.state.isLocating = false;
        this.setGeoBtnLoading(false);
        const { latitude, longitude } = position.coords;
        const zoom = this.computeOptimalZoom();
        this.state.map.setView([latitude, longitude], zoom);
        this.elements.searchInput.value = "";
        this.hideSearchResults();
        this.trackEvent("dorpi_geolocation_success");
      },
      (error) => {
        this.state.isLocating = false;
        this.setGeoBtnLoading(false);
        // error.code 1 === PERMISSION_DENIED
        const denied = error && error.code === 1;
        this.showGeoMsg(denied ? "denied" : "error");
        this.trackEvent("dorpi_geolocation_failed", {
          reason: denied ? "denied" : "unavailable",
        });
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  }

  /** Toggle the location button's loading state (disabled + label swap). */
  setGeoBtnLoading(isLoading) {
    const { geoBtn, geoBtnLabel } = this.elements;
    if (!geoBtn) return;
    geoBtn.disabled = isLoading;
    geoBtn.classList.toggle("is-loading", isLoading);
    if (geoBtnLabel) {
      if (isLoading) {
        this._geoBtnDefaultLabel =
          this._geoBtnDefaultLabel || geoBtnLabel.textContent;
        geoBtnLabel.textContent = "Locatie ophalen...";
      } else if (this._geoBtnDefaultLabel) {
        geoBtnLabel.textContent = this._geoBtnDefaultLabel;
      }
    }
  }

  /** Show the geolocation feedback message. `kind` is "denied" or "error". */
  showGeoMsg(kind) {
    const el = this.elements.geoMsg;
    if (!el) return;
    const text =
      kind === "denied"
        ? el.dataset.deniedText
        : el.dataset.errorText;
    el.textContent = text || "Locatie kon niet worden opgehaald.";
    el.hidden = false;
  }

  hideGeoMsg() {
    if (this.elements.geoMsg) this.elements.geoMsg.hidden = true;
  }

  /**
   * Calculate bounding box dimensions based on aspect ratio and size
   * Uses A5 paper ratio (1:sqrt(2))
   */
  calculateBboxDimensions() {
    const SQRT2 = Math.SQRT2;

    const ratios = {
      portrait: { width: 1, height: SQRT2 },
      landscape: { width: SQRT2, height: 1 },
    };

    const ratio = ratios[this.state.aspectRatio];
    const maxDimension = Math.max(ratio.width, ratio.height);
    const scale = this.state.size / maxDimension;

    return {
      width: ratio.width * scale,
      height: ratio.height * scale,
    };
  }

  /**
   * Convert meters to lat/lng offsets
   * @param {number} meters - Distance in meters
   * @param {number} lat - Latitude for calculation
   * @returns {{ latOffset: number, lngOffset: number }}
   */
  metersToLatLng(meters, lat) {
    const earthRadius = 6371000;
    const latOffset = (meters / earthRadius) * (180 / Math.PI);
    const lngOffset =
      (meters / (earthRadius * Math.cos((lat * Math.PI) / 180))) *
      (180 / Math.PI);
    return { latOffset, lngOffset };
  }

  async searchLocation(query) {
    if (query.length < 3) {
      this.hideSearchResults();
      return;
    }

    this.elements.searchResults.innerHTML =
      '<div class="dorpi-map-selector__searching">Zoeken...</div>';
    this.elements.searchResults.hidden = false;

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&countrycodes=nl&addressdetails=1&q=${encodeURIComponent(query)}&limit=5`,
      );
      const results = await response.json();
      // Belt-and-suspenders: only keep addresses Nominatim confirms are in NL.
      const dutchResults = results.filter(
        (result) => result.address && result.address.country_code === "nl",
      );
      this.showSearchResults(dutchResults);
    } catch (error) {
      console.error("Search error:", error);
      this.elements.searchResults.innerHTML =
        '<div class="dorpi-map-selector__no-results">Zoeken mislukt</div>';
    }
  }

  showSearchResults(results) {
    const container = this.elements.searchResults;

    if (results.length === 0) {
      container.innerHTML =
        '<div class="dorpi-map-selector__no-results">Geen resultaten</div>';
    } else {
      container.innerHTML = results
        .map(
          (result) => `
        <button
          type="button"
          class="dorpi-map-selector__search-result"
          data-lat="${result.lat}"
          data-lon="${result.lon}"
          data-name="${this.escapeHtml(result.display_name)}"
        >
          ${this.escapeHtml(result.display_name)}
        </button>
      `,
        )
        .join("");

      container.querySelectorAll("[data-lat]").forEach((btn) => {
        btn.addEventListener("click", () => {
          const lat = parseFloat(btn.dataset.lat);
          const lon = parseFloat(btn.dataset.lon);
          this.selectLocation(lat, lon, btn.dataset.name);
        });
      });
    }

    container.hidden = false;
  }

  hideSearchResults() {
    this.elements.searchResults.hidden = true;
  }

  selectLocation(lat, lon, name) {
    const zoom = this.computeOptimalZoom();
    this.state.map.setView([lat, lon], zoom);
    this.elements.searchInput.value = name;
    this.hideSearchResults();
  }

  setAspectRatio(ratio) {
    this.state.aspectRatio = ratio;

    this.elements.ratioButtons.forEach((btn) => {
      btn.classList.toggle("is-active", btn.dataset.ratio === ratio);
    });

    this.updateRectPixelSize();
    const zoom = this.computeOptimalZoom();
    const center = this.state.map.getCenter();
    this.state.map.setView(center, zoom, { animate: false });
    // moveend fires synchronously after setView → updateBboxFromCenter
  }

  setFrame(frame) {
    this.state.frame = frame;

    this.elements.frameButtons.forEach((btn) => {
      btn.classList.toggle("is-active", btn.dataset.frame === frame);
    });

    this.updatePreview();
  }

  /**
   * Swap the product-preview photo to match the selected frame. Falls back to
   * whichever image is set if the chosen frame has no photo. No-op when the
   * merchant hasn't configured any preview images.
   */
  updatePreview() {
    const img = this.elements.previewImg;
    if (!img) return;

    const zwart = img.dataset.previewZwart;
    const eikenhout = img.dataset.previewEikenhout;
    const next = this.state.frame === "eikenhout" ? eikenhout : zwart;
    const src = next || zwart || eikenhout;
    if (src && img.getAttribute("src") !== src) {
      img.setAttribute("src", src);
    }
  }

  setSize(size) {
    this.state.size = size;
    this.elements.sizeDisplay.textContent = size;
    this.updateSliderBackground();

    // Throttle zoom updates to one per animation frame so rapid slider events
    // don't queue up multiple Leaflet redraws, eliminating the laggy interrupts.
    cancelAnimationFrame(this._zoomRaf);
    this._zoomRaf = requestAnimationFrame(() => {
      const zoom = this.computeOptimalZoom();
      const center = this.state.map.getCenter();
      this.state.map.setView(center, zoom, { animate: false });
      // moveend fires synchronously after setView → updateBboxFromCenter
    });
  }

  updateSliderBackground() {
    const slider = this.elements.sizeSlider;
    const min = parseInt(slider.min, 10);
    const max = parseInt(slider.max, 10);
    const value = this.state.size;
    const fraction = (value - min) / (max - min);

    // The thumb centre only travels from half-a-thumb in from the left to
    // half-a-thumb in from the right, so the fill boundary must follow that
    // inset track — otherwise it drifts from the thumb, worst at the ends.
    const THUMB = 24; // px, matches the CSS thumb (1.5rem)
    const w = slider.clientWidth;
    let percentage;
    if (w > THUMB) {
      const centerPx = THUMB / 2 + fraction * (w - THUMB);
      percentage = (centerPx / w) * 100;
    } else {
      percentage = fraction * 100;
    }

    slider.style.background = `linear-gradient(to right, #46493c 0%, #46493c ${percentage}%, #ded6c7 ${percentage}%, #ded6c7 100%)`;
  }

  updateGenerateButton() {
    const btn = this.elements.generateBtn;
    btn.disabled = this.state.isGenerating;
    if (!this.state.isGenerating) {
      btn.textContent = "Verder";
    }
  }

  showError(message) {
    this.elements.error.textContent = message;
    this.elements.error.hidden = false;
  }

  hideError() {
    this.elements.error.hidden = true;
  }

  async generateSTL() {
    if (!this.state.currentBbox || this.state.isGenerating) return;

    this.trackEvent("dorpi_verder_click", {
      map_size: this.state.size,
      aspect_ratio: this.state.aspectRatio,
      frame: this.state.frame,
    });

    this.state.isGenerating = true;
    this.startLoadingSequence();
    this.hideError();
    this.updateGenerateButton();

    try {
      const response = await fetch(`${this.config.apiUrl}/generate-stl`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
        },
        body: JSON.stringify({ ...this.state.currentBbox, frame: this.state.frame }),
      });

      // The endpoint streams Server-Sent Events. If we didn't get a readable
      // stream back (e.g. an error response), fall back to reading it as JSON.
      if (!response.ok || !response.body) {
        let detail = "Genereren mislukt";
        try {
          const data = await response.json();
          detail = data.detail || detail;
        } catch (_) {
          /* non-JSON error body — keep the default message */
        }
        this.showError(detail);
        this.stopLoadingSequence();
        return;
      }

      const result = await this.consumeProgressStream(response.body);

      if (result && result.type === "done") {
        const stlUrl = `${this.config.apiUrl}${result.download_url}`;
        // Snap the bar to 100% and keep the loading screen up — the page navigates
        // to the viewer next, so the overlay stays until unload rather than sliding
        // away first.
        this.finishLoadingBar();
        this.navigateToViewer(stlUrl);
        return;
      }

      this.showError((result && result.detail) || "Genereren mislukt");
      this.stopLoadingSequence();
    } catch (error) {
      console.error("Generate error:", error);
      this.showError("Verbinding met server mislukt");
      this.stopLoadingSequence();
    } finally {
      this.state.isGenerating = false;
      this.updateGenerateButton();
    }
  }

  /**
   * Read the SSE body to completion, applying each progress event to the loading
   * bar. Resolves with the terminating `done`/`error` event object, or null if the
   * stream ended without one.
   */
  async consumeProgressStream(body) {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;

      // Normalise CRLF so the "\n\n" event delimiter matches regardless of proxy.
      buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n");

      let sep;
      while ((sep = buffer.indexOf("\n\n")) !== -1) {
        const rawEvent = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);

        const data = rawEvent
          .split("\n")
          .filter((line) => line.startsWith("data:"))
          .map((line) => line.slice(5).trim())
          .join("\n");
        if (!data) continue;

        let payload;
        try {
          payload = JSON.parse(data);
        } catch (_) {
          continue;
        }

        // Only the real `save` event moves the bar; earlier stages are faked on a
        // timer (see startLoadingSequence), so we ignore their progress events.
        if (payload.type === "progress" && payload.stage === "save") {
          this._clearFakeTimeline();
          // Scroll the bar to 78%, then crawl to 100%. The message sequence owns
          // the label (it leads with "3D-model genereren").
          this.enterSaveStage();
          this.startSaveMessageSequence();
        } else if (payload.type === "done" || payload.type === "error") {
          return payload;
        }
      }
    }

    return null;
  }

  // ── Loading screen ─────────────────────────────────────────────────────────

  startLoadingSequence() {
    const { loading, loadingBar, loadingMsg } = this.elements;
    if (!loading) return;

    // Reset the bar, then slide the overlay up.
    this._currentStageLabel = null;
    this._clearFakeTimeline();
    if (loadingBar) {
      loadingBar.style.transition = "none";
      loadingBar.style.width = "0%";
    }
    const first = DORPI_LOADING_STAGES.download;
    if (loadingMsg) {
      loadingMsg.textContent = first.label;
      this._currentStageLabel = first.label;
    }
    loading.classList.add("is-active");

    // Force reflow so the width reset + is-active apply before we animate.
    void loading.offsetWidth;

    // Fake every stage up to (and including) mesh on a timer: apply each in turn
    // after the previous stages' combined average durations. The bar climbs to
    // mesh's target and holds there until the real `save` event takes over.
    const fakeStages = ["download", "load_terrain", "buildings", "process", "mesh"];
    this._fakeTimers = [];
    let elapsedMs = 0;
    fakeStages.forEach((stageId, i) => {
      if (i === 0) {
        this.applyLoadingStage(stageId);
      } else {
        this._fakeTimers.push(
          setTimeout(() => this.applyLoadingStage(stageId), elapsedMs)
        );
      }
      elapsedMs += DORPI_LOADING_STAGES[stageId].duration * 1000;
    });
  }

  /**
   * Enter the real save stage. Always scroll the bar up to the mesh target (78%)
   * first — so it starts from a consistent point even when the real save event
   * arrives before the faked bar reached it — then crawl to 100% over the save
   * duration.
   */
  enterSaveStage() {
    const bar = this.elements.loadingBar;
    if (!bar) return;
    const meshTarget = DORPI_LOADING_STAGES.mesh.target; // 78%
    const save = DORPI_LOADING_STAGES.save;

    bar.style.transition = "width 0.5s ease-out";
    bar.style.width = `${meshTarget}%`;

    this._fakeTimers.push(
      setTimeout(() => {
        bar.style.transition = `width ${save.duration}s linear`;
        bar.style.width = `${save.target}%`;
      }, 500)
    );
  }

  /**
   * Once the real `save` stage starts, rotate messages while the bar finishes
   * easing to 100%:
   *    0–6s   "3D-model genereren"
   *    6–10s  "Model opslaan"
   *   10–15s  "Klaarmaken om te openen"
   *   15s → done  "Nog even geduld a.u.b."
   * The bar itself keeps animating — this only swaps the text.
   */
  startSaveMessageSequence() {
    const meshLabel = DORPI_LOADING_STAGES.mesh.label; // "3D-model genereren"
    // Lead with the mesh label. If it's already showing (real save arrived after
    // the faked mesh stage), leave it as-is to avoid a redundant fade.
    if (this._currentStageLabel !== meshLabel) {
      this._currentStageLabel = meshLabel;
      this.swapLoadingMessage(meshLabel);
    }

    this._fakeTimers.push(
      setTimeout(() => this.swapLoadingMessage("Model opslaan"), 6000)
    );
    this._fakeTimers.push(
      setTimeout(() => this.swapLoadingMessage("Klaarmaken om te openen"), 10000)
    );
    this._fakeTimers.push(
      setTimeout(
        () => this.swapLoadingMessage("Nog even geduld a.u.b."),
        15000
      )
    );
  }

  /** Cancel any pending faked-stage timers. */
  _clearFakeTimeline() {
    if (this._fakeTimers) this._fakeTimers.forEach(clearTimeout);
    this._fakeTimers = [];
  }

  /**
   * Drive the bar/message for a stage reported by the server. Eases the bar toward
   * the stage's target width over its expected duration; if the next stage arrives
   * early the bar simply retargets, and if it arrives late the bar holds at target.
   */
  applyLoadingStage(stageId, { skipLabel = false } = {}) {
    const stage = DORPI_LOADING_STAGES[stageId];
    if (!stage) return;

    const { loadingBar } = this.elements;
    if (loadingBar) {
      loadingBar.style.transition = `width ${stage.duration}s linear`;
      loadingBar.style.width = `${stage.target}%`;
    }

    if (!skipLabel && stage.label && stage.label !== this._currentStageLabel) {
      this._currentStageLabel = stage.label;
      this.swapLoadingMessage(stage.label);
    }
  }

  /** Fill the bar to 100% quickly once the model is ready, before navigating. */
  finishLoadingBar() {
    this._clearFakeTimeline();
    const { loadingBar } = this.elements;
    if (loadingBar) {
      loadingBar.style.transition = "width 0.4s ease-out";
      loadingBar.style.width = "100%";
    }
  }

  swapLoadingMessage(text) {
    const el = this.elements.loadingMsg;
    if (!el) return;
    el.classList.add("is-fading");
    setTimeout(() => {
      el.textContent = text;
      el.classList.remove("is-fading");
    }, 300);
  }

  stopLoadingSequence() {
    const { loading, loadingBar } = this.elements;
    this._clearFakeTimeline();
    this._currentStageLabel = null;
    if (loading) loading.classList.remove("is-active");
    if (loadingBar) {
      loadingBar.style.transition = "none";
      loadingBar.style.width = "0%";
    }
  }

  navigateToViewer(stlUrl) {
    const viewerData = {
      stlUrl,
      coordinates: `${this.state.currentBbox.min_x} ${this.state.currentBbox.min_y} ${this.state.currentBbox.max_x} ${this.state.currentBbox.max_y}`,
      aspectRatio: this.state.aspectRatio,
      frame: this.state.frame,
      size: this.state.size,
    };

    this.trackEvent("dorpi_model_redirect", {
      map_size: this.state.size,
      aspect_ratio: this.state.aspectRatio,
      frame: this.state.frame,
      time_on_page_seconds: this.getTimeOnPage(),
    });

    localStorage.setItem("dorpi_viewer_data", JSON.stringify(viewerData));
    window.location.href = this.config.viewerUrl;
  }

  // ── Tracking helpers ────────────────────────────────────────────────────────

  trackEvent(eventName, params = {}) {
    if (typeof gtag === "function") {
      gtag("event", eventName, params);
    }
  }

  getTimeOnPage() {
    return Math.round((performance.now() - this.pageEntryTime) / 1000);
  }

  trackTimeOnPage() {
    this.trackEvent("dorpi_map_selector_exit", {
      time_on_page_seconds: this.getTimeOnPage(),
    });
  }

  /**
   * Escape HTML to prevent XSS
   * @param {string} text
   * @returns {string}
   */
  escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }
}

// Initialize on DOM ready
document.addEventListener("DOMContentLoaded", () => {
  const wrapper = document.querySelector(".dorpi-map-selector__wrapper");
  if (wrapper) {
    setTimeout(() => {
      new DorpiMapSelector(wrapper);
    }, 100);
  }
});
